import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";

const POLL_MS = 4000;
const STATUS_POLL_MS = 15000;

export const Chat = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setMessages(data);
    } catch {
      // ignore
    }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/presence/admin-status");
      setAdminOnline(!!data?.online);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
    loadStatus();
    const t1 = setInterval(load, POLL_MS);
    const t2 = setInterval(loadStatus, STATUS_POLL_MS);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { content: content.trim() });
      setContent("");
      await load();
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div data-testid="dashboard-chat" className="border border-white/10 grid grid-cols-1 lg:grid-cols-12">
      <aside className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-white/10 bg-zinc-900/30 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("chat.title")}</p>
        <div className="flex items-center gap-3 pb-5 border-b border-white/10">
          <div className="w-11 h-11 bg-zinc-800 flex items-center justify-center font-display text-base">VS</div>
          <div>
            <p className="text-sm font-medium">Versant Studio</p>
            {adminOnline ? (
              <p data-testid="admin-online" className="text-xs text-emerald-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-400 inline-block animate-pulse" />
                {t("chat.online")}
              </p>
            ) : (
              <p data-testid="admin-offline" className="text-xs text-zinc-500 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-zinc-500 inline-block" />
                {t("chat.offline")}
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-5 leading-relaxed">{t("chat.aside_note")}</p>
      </aside>

      <div className="lg:col-span-8 flex flex-col h-[600px]">
        <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-6 space-y-4 bg-zinc-950">
          {messages.length === 0 && (
            <p className="text-zinc-500 text-sm text-center mt-10">{t("chat.empty")}</p>
          )}
          {messages.map((m) => {
            const isUser = m.sender === "user";
            return (
              <div key={m.message_id} className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
                {!isUser && (
                  <div className="w-8 h-8 bg-zinc-800 flex-shrink-0 flex items-center justify-center font-display text-xs">
                    VS
                  </div>
                )}
                <div className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed ${
                  isUser ? "bg-white text-zinc-950" : "bg-zinc-900 text-zinc-100 border border-white/10"
                }`}>
                  {m.content}
                  <div className={`text-[10px] mt-1.5 font-mono ${isUser ? "text-zinc-600" : "text-zinc-500"}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                {isUser && (
                  <div className="w-8 h-8 bg-white text-zinc-950 flex-shrink-0 flex items-center justify-center font-display text-xs">
                    {initials}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <form onSubmit={send} data-testid="chat-form" className="border-t border-white/10 p-4 flex items-center gap-3 bg-zinc-950">
          <input
            data-testid="chat-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t("chat.placeholder")}
            className="flex-1 bg-zinc-900 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
          />
          <button
            type="submit"
            disabled={sending || !content.trim()}
            data-testid="chat-send"
            className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-3 text-sm disabled:opacity-50"
          >
            <Send size={14} />
            {t("chat.send")}
          </button>
        </form>
      </div>
    </div>
  );
};
